import React, { useEffect, useState } from 'react';
import { X, ChevronLeft } from 'lucide-react';
import OpenTableWidget from './OpenTableWidget';
import { useSite } from '../lib/content';

/**
 * Floating reservation modal.
 * - From a venue page (lockVenue) → opens that venue's OpenTable widget directly.
 * - From the global "Reservations" button → first shows a venue chooser
 *   (The Steakhouse / The Tavern), then loads only the selected venue's widget.
 * Restaurant IDs and copy are CMS-editable via the `site` page.
 */
const ReservationModal = ({ open, onClose, lockVenue = null }) => {
  const site = useSite();
  const ot = site.opentable || {};
  const r = site.reservation || {};
  const ALL_VENUES = [
    { key: 'steakhouse', label: 'The Steakhouse', rid: ot.steakhouse || '1234111' },
    { key: 'tavern', label: 'The Tavern', rid: ot.tavern || '1242793' },
  ];
  const VENUES = lockVenue ? ALL_VENUES.filter((v) => v.key === lockVenue) : ALL_VENUES;

  // Locked → that venue. Single venue → preselect. Otherwise → null (force choice).
  const initialVenue = lockVenue || (VENUES.length === 1 ? VENUES[0].key : null);
  const [venue, setVenue] = useState(initialVenue);

  // Reset the selection each time the modal (re)opens — adjust state during render
  // (React-recommended) instead of in an effect.
  const [wasOpen, setWasOpen] = useState(open);
  if (open !== wasOpen) {
    setWasOpen(open);
    if (open) setVenue(initialVenue);
  }

  useEffect(() => {
    document.body.style.overflow = open ? 'hidden' : '';
    return () => {
      document.body.style.overflow = '';
    };
  }, [open]);

  if (!open) return null;

  const active = VENUES.find((v) => v.key === venue) || null;
  const canChange = !lockVenue && VENUES.length > 1;

  return (
    <div
      data-testid="reservation-modal"
      className="fixed inset-0 z-[100] flex items-start sm:items-center justify-center p-4 sm:p-8 overflow-y-auto"
    >
      <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" onClick={onClose} aria-hidden />

      <div className="relative bg-[#942222] text-[#f1ead9] w-full max-w-[640px] border border-[#e2b95a]/40 shadow-2xl">
        <button
          onClick={onClose}
          aria-label="Close reservation"
          data-testid="reservation-modal-close"
          className="absolute top-4 right-4 p-2 text-[#f1ead9]/80 hover:text-[#e2b95a] transition-colors"
        >
          <X size={22} />
        </button>

        <div className="px-8 sm:px-12 pt-12 pb-6 text-center">
          <p className="text-[10px] tracking-[0.42em] uppercase text-[#e2b95a]">
            {r.eyebrow || 'Reservations'}
          </p>
          <h2 className="font-serif text-3xl sm:text-4xl mt-3 tracking-tight">
            {active ? active.label : (r.title || 'Where would you like to dine?')}
          </h2>
          <p className="mt-3 text-[#f1ead9]/75 text-sm leading-relaxed max-w-sm mx-auto">
            {active
              ? 'Real-time availability powered by OpenTable.'
              : 'Choose a venue to reserve your table directly via OpenTable.'}
          </p>
        </div>

        {/* Venue chooser (only when no venue is selected) */}
        {!active && (
          <div className="px-8 sm:px-12 pb-10">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {VENUES.map((v) => (
                <button
                  key={v.key}
                  onClick={() => setVenue(v.key)}
                  data-testid={`reservation-choose-${v.key}`}
                  className="py-6 px-4 text-sm tracking-[0.22em] uppercase font-semibold border border-[#f1ead9]/30 text-[#f1ead9] hover:bg-[#e2b95a] hover:text-[#942222] hover:border-[#e2b95a] transition-colors"
                >
                  {v.label}
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Selected venue widget */}
        {active && (
          <>
            {canChange && (
              <div className="px-8 sm:px-12">
                <button
                  onClick={() => setVenue(null)}
                  data-testid="reservation-change-venue"
                  className="inline-flex items-center gap-1 text-[10px] tracking-[0.28em] uppercase text-[#e2b95a] hover:opacity-80 transition-opacity"
                >
                  <ChevronLeft size={14} /> Change venue
                </button>
              </div>
            )}

            <div className="px-6 sm:px-10 py-8">
              <div className="bg-[#f7f1e3] py-6 px-2 min-h-[280px] flex items-center justify-center">
                <OpenTableWidget key={active.rid} rid={active.rid} theme="standard" />
              </div>
            </div>

            <div className="px-8 sm:px-12 pb-8 text-center">
              <p className="text-[10px] tracking-[0.3em] uppercase text-[#f1ead9]/60">
                Powered by OpenTable
              </p>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default ReservationModal;
